import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    Alert,
    Badge,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PreviewIcon from '@mui/icons-material/Preview';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`preview-tabpanel-${index}`}
            aria-labelledby={`preview-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 2 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const PreviewUploadDialog = ({ 
    isVisible, 
    onClose, 
    previewData, 
    onConfirm,
    onShowConflicts,
    loading 
}) => {
    const { t, i18n } = useTranslation();
    const dir = i18n.dir();
    const [activeTab, setActiveTab] = useState(0);

    if (!previewData) {
        return null;
    }

    const { newCards = [], updatedCards = [], conflicts = [] } = previewData;
    const hasConflicts = conflicts.length > 0;

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const getTotalChanges = () => {
        return newCards.length + updatedCards.length + conflicts.length;
    };



    return (
        <Dialog
            open={isVisible}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            dir={dir}
        >
            <DialogTitle sx={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #304FFF' }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <PreviewIcon sx={{ color: '#304FFF' }} />
                    <Typography variant="h6" component="span">
                        {t('PreviewAccountCardImport') || 'Preview Account Card Import'}
                    </Typography>
                    <Chip 
                        label={`${getTotalChanges()} ${t('Changes') || 'Changes'}`} 
                        color="primary" 
                        size="small"
                        sx={{ ml: 'auto' }}
                    />
                </Box>
            </DialogTitle>

            <DialogContent sx={{ mt: 2, minHeight: '400px' }}>
                <Box dir={dir}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        {t('PreviewExplanation') || 
                        'Review the changes below before importing. You can see which cards will be created, updated, or have conflicts.'}
                    </Typography>
                </Alert>

                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={activeTab} onChange={handleTabChange} aria-label="preview tabs">
                        <Tab 
                            label={
                                <Badge badgeContent={newCards.length} color="success">
                                    <Box display="flex" alignItems="center" gap={1} px={1}>
                                        <AddCircleIcon fontSize="small" />
                                        {t('NewCards') || 'New Cards'}
                                    </Box>
                                </Badge>
                            } 
                        />
                        <Tab 
                            label={
                                <Badge badgeContent={updatedCards.length} color="primary">
                                    <Box display="flex" alignItems="center" gap={1} px={1}>
                                        <EditIcon fontSize="small" />
                                        {t('UpdatedCards') || 'Updated Cards'}
                                    </Box>
                                </Badge>
                            } 
                        />
                        <Tab 
                            label={
                                <Badge badgeContent={conflicts.length} color="warning">
                                    <Box display="flex" alignItems="center" gap={1} px={1}>
                                        <WarningIcon fontSize="small" />
                                        {t('Conflicts') || 'Conflicts'}
                                    </Box>
                                </Badge>
                            } 
                        />
                    </Tabs>
                </Box>

                <TabPanel value={activeTab} index={0}>
                    {newCards.length > 0 ? (
                        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>{t('CardID') || 'Card ID'}</strong></TableCell>
                                        <TableCell><strong>{t('Name') || 'Name'}</strong></TableCell>
                                        <TableCell><strong>{t('CompanyId') || 'Company ID'}</strong></TableCell>
                                        <TableCell><strong>{t('PalCode') || 'PAL Code'}</strong></TableCell>
                                        <TableCell><strong>{t('PhoneNumber') || 'Phone'}</strong></TableCell>
                                        <TableCell><strong>{t('Email') || 'Email'}</strong></TableCell>
                                        <TableCell><strong>{t('Location') || 'Location'}</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {newCards.map((card, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>{card.cardId}</TableCell>
                                            <TableCell>{card.name}</TableCell>
                                            <TableCell>{card.accountCaseId || '-'}</TableCell>
                                            <TableCell>{card.palCode || '-'}</TableCell>
                                            <TableCell>{card.phoneNumber || '-'}</TableCell>
                                            <TableCell>{card.email || '-'}</TableCell>
                                            <TableCell>{card.location || '-'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box textAlign="center" py={4}>
                            <Typography variant="body2" color="textSecondary">
                                {t('NoNewCards') || 'No new cards to create'}
                            </Typography>
                        </Box>
                    )}
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                    {updatedCards.length > 0 ? (
                        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>{t('CardID') || 'Card ID'}</strong></TableCell>
                                        <TableCell><strong>{t('Field') || 'Field'}</strong></TableCell>
                                        <TableCell><strong>{t('OldValue') || 'Old Value'}</strong></TableCell>
                                        <TableCell><strong>{t('NewValue') || 'New Value'}</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {updatedCards.map((card, index) => {
                                        const changes = [];
                                        
                                        // Check all possible field changes
                                        if (card.name && card.name !== card.oldName) {
                                            changes.push({ field: t('Name'), old: card.oldName || '-', new: card.name });
                                        }
                                        if (card.accountCaseId !== card.oldAccountCaseId) {
                                            changes.push({ field: t('CompanyId'), old: card.oldAccountCaseId || '-', new: card.accountCaseId || '-' });
                                        }
                                        if (card.palCode !== card.oldPalCode) {
                                            changes.push({ field: t('PalCode'), old: card.oldPalCode || '-', new: card.palCode || '-' });
                                        }
                                        if (card.phoneNumber && card.phoneNumber !== card.oldPhoneNumber) {
                                            changes.push({ field: t('PhoneNumber'), old: card.oldPhoneNumber || '-', new: card.phoneNumber });
                                        }
                                        if (card.email && card.email !== card.oldEmail) {
                                            changes.push({ field: t('Email'), old: card.oldEmail || '-', new: card.email });
                                        }
                                        if (card.location && card.location !== card.oldLocation) {
                                            changes.push({ field: t('Location'), old: card.oldLocation || '-', new: card.location });
                                        }
                                        if (card.sortCodeId && card.sortCodeId !== card.oldSortCodeId) {
                                            changes.push({ field: t('SortCode'), old: card.oldSortCodeId || '-', new: card.sortCodeId });
                                        }
                                        if (card.autoProcessed !== card.oldAutoProcessed) {
                                            changes.push({ 
                                                field: t('AutoProcessed'), 
                                                old: card.oldAutoProcessed ? t('Yes') : t('No'), 
                                                new: card.autoProcessed ? t('Yes') : t('No') 
                                            });
                                        }
                                        
                                        // If no changes detected, show at least the card ID
                                        if (changes.length === 0) {
                                            changes.push({ field: '-', old: '-', new: '-' });
                                        }
                                        
                                        return changes.map((change, changeIndex) => (
                                            <TableRow key={`${index}-${changeIndex}`} hover>
                                                {changeIndex === 0 && (
                                                    <TableCell rowSpan={changes.length} sx={{ fontWeight: 'bold' }}>
                                                        {card.cardId}
                                                    </TableCell>
                                                )}
                                                <TableCell>{change.field}</TableCell>
                                                <TableCell sx={{ backgroundColor: '#fff3cd' }}>{change.old}</TableCell>
                                                <TableCell sx={{ backgroundColor: '#d1f2eb', fontWeight: 'bold' }}>{change.new}</TableCell>
                                            </TableRow>
                                        ));
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box textAlign="center" py={4}>
                            <Typography variant="body2" color="textSecondary">
                                {t('NoUpdatedCards') || 'No cards to update'}
                            </Typography>
                        </Box>
                    )}
                </TabPanel>

                <TabPanel value={activeTab} index={2}>
                    {conflicts.length > 0 ? (
                        <Box>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    {t('ConflictPreviewExplanation') || 
                                    'These cards have conflicts (same ID but different names). New cards have been created with new IDs. Only cards with existing transactions are shown.'}
                                </Typography>
                            </Alert>
                            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>{t('IncomingCardID') || 'Incoming ID'}</strong></TableCell>
                                            <TableCell><strong>{t('IncomingName') || 'Incoming Name'}</strong></TableCell>
                                            <TableCell align="center"><SwapHorizIcon /></TableCell>
                                            <TableCell><strong>{t('ExistingCardID') || 'Existing ID'}</strong></TableCell>
                                            <TableCell><strong>{t('ExistingName') || 'Existing Name'}</strong></TableCell>
                                            <TableCell><strong>{t('NewCardID') || 'New ID'}</strong></TableCell>
                                            <TableCell align="center"><strong>{t('Transactions') || 'Trans.'}</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {conflicts.map((conflict, index) => (
                                            <TableRow key={index} hover>
                                                <TableCell>{conflict.incomingCardId}</TableCell>
                                                <TableCell>{conflict.incomingName}</TableCell>
                                                <TableCell align="center"><SwapHorizIcon color="action" fontSize="small" /></TableCell>
                                                <TableCell sx={{ backgroundColor: '#fff3cd', fontWeight: 'bold' }}>
                                                    {conflict.existingCardId}
                                                </TableCell>
                                                <TableCell sx={{ backgroundColor: '#fff3cd' }}>
                                                    {conflict.existingName}
                                                </TableCell>
                                                <TableCell sx={{ backgroundColor: '#d1f2eb', fontWeight: 'bold' }}>
                                                    {conflict.newCardId}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                                                    {conflict.transactionCount || 0}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Button
                                variant="outlined"
                                color="warning"
                                fullWidth
                                sx={{ mt: 2 }}
                                onClick={onShowConflicts}
                                startIcon={<WarningIcon />}
                            >
                                {t('ResolveConflictsLater') || 'Resolve Conflicts After Import'}
                            </Button>
                        </Box>
                    ) : (
                        <Box textAlign="center" py={4}>
                            <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50', mb: 2 }} />
                            <Typography variant="body2" color="textSecondary">
                                {t('NoConflicts') || 'No conflicts detected - all clear!'}
                            </Typography>
                        </Box>
                    )}
                </TabPanel>
                </Box>
            </DialogContent>

            <DialogActions sx={{ padding: 2, borderTop: '1px solid #e0e0e0', gap: 1 }}>
                <Box flex={1}>
                    {hasConflicts && (
                        <Alert severity="warning" sx={{ py: 0 }}>
                            <Typography variant="caption">
                                {t('ConflictWarning') || `${conflicts.length} conflict(s) detected with transactions`}
                            </Typography>
                        </Alert>
                    )}
                </Box>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    disabled={loading}
                >
                    {t('Cancel') || 'Cancel'}
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                >
                    {loading 
                        ? (t('Importing') || 'Importing...') 
                        : (t('ConfirmImport') || 'Confirm Import')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PreviewUploadDialog;

